import { formatDateTimeToBR, formatDateToBR } from "@utils/FormatDate";

export function getChoiceText(choices, value) {
  if (!Array.isArray(choices)) return value;

  // Para múltipla escolha
  if (Array.isArray(value)) {
    return value.map(v => {
      const found = choices.find(c => c.value === v);
      return found ? found.text : v;
    }).join(", ");
  }
  // Para valor único
  const found = choices.find(c => c.value === value);
  return found ? found.text : value;
}

export function renderMatrix(resposta) {
  if (!resposta || typeof resposta !== "object" || Array.isArray(resposta)) return "-";
  return Object.entries(resposta)
    .map(([linha, coluna]) => `${linha}: ${coluna}`)
    .join("; ");
}

export function renderMatrixDropdown(resposta) {
  if (!resposta || typeof resposta !== "object" || Array.isArray(resposta)) return "-";
  return Object.entries(resposta)
    .map(([linha, cols]) => {
      if (typeof cols === "object")
        return `${linha}: ` +
          Object.entries(cols).map(([col, val]) => `${col}=${val}`).join(", ");
      return `${linha}: ${cols}`;
    })
    .join("; ");
}

export function renderMatrixDynamic(resposta) {
  if (!Array.isArray(resposta)) return "-";
  return resposta
    .map((cols, idx) =>
      `Linha ${idx + 1}: ` +
      Object.entries(cols).map(([col, val]) => `${col}=${val}`).join(", ")
    )
    .join("; ");
}

export function renderGenericObject(resposta) {
  if (!resposta || typeof resposta !== "object") return "-";
  return Object.entries(resposta)
    .map(([k, v]) => `${k}: ${v}`)
    .join('; ');
}

export function renderArray(resposta) {
  if (!Array.isArray(resposta)) return "-";
  return resposta.join(", ");
}

// Booleano customizado
export function renderBoolean(resposta, pergunta) {
  if (typeof resposta !== "boolean") return "-";
  if (pergunta?.labelTrue && pergunta?.labelFalse) {
    return resposta ? pergunta.labelTrue : pergunta.labelFalse;
  }
  return resposta ? "Sim" : "Não";
}

// Rating/Avaliação
export function renderRating(resposta, pergunta) {
  if (pergunta?.rateValues && Array.isArray(pergunta.rateValues)) {
    const found = pergunta.rateValues.find(
      v => v.value == resposta
    );
    if (found) {
      // Exibe o número + texto (ex: "3 - Bom")
      return `${resposta} - ${found.text ?? found.value}`;
    }
  }
  // fallback
  return resposta != null ? resposta : "-";
}

// Ranking/Classificação (array de values)
export function renderRanking(resposta, pergunta) {
  if (!Array.isArray(resposta)) return "-";
  // Se Choices disponível, mostra texto, senão value mesmo
  if (pergunta?.choices) {
    return resposta
      .map((v, idx) => {
        const found = pergunta.choices.find(ch => ch.value == v);
        return `${idx + 1}. ${found?.text ?? v}`;
      })
      .join(", ");
  }
  return resposta.map((v, idx) => `${idx + 1}. ${v}`).join(", ");
}

export const IGNORED_TYPES = ['signaturepad', 'file', 'imagepicker', 'photo', 'drawing'];

export function formatRespostaParaDisplay(resposta, pergunta) {
  // Se não existe pergunta (campo não se aplica)
  if (!pergunta) return "-";

  // Se a resposta é null, undefined, string vazia ou só espaços
  if (
    resposta === null ||
    resposta === undefined ||
    (typeof resposta === "string" && resposta.trim() === "")
  ) {
    return "N/A";
  }
  // Datas
  if (pergunta.inputType === "date") {
    return formatDateToBR(resposta);
  }
  if (pergunta.inputType === "datetime-local") {
    return formatDateTimeToBR(resposta);
  }

  // Booleanos
  if (pergunta.type === "boolean") {
    return renderBoolean(resposta, pergunta);
  }

  // Rating
  if (pergunta.type === "rating") {
    return renderRating(resposta, pergunta);
  }

  // Ranking
  if (pergunta.type === "ranking") {
    return renderRanking(resposta, pergunta);
  }

  // Choices
  if (pergunta.choices && Array.isArray(pergunta.choices) && pergunta.choices.length > 0) {
    const display = getChoiceText(pergunta.choices, resposta);
    return display ?? "-";
  }

  // Matrizes
  if (pergunta.type === "matrix") {
    return renderMatrix(resposta);
  }
  if (pergunta.type === "matrixdropdown") {
    return renderMatrixDropdown(resposta);
  }
  if (pergunta.type === "matrixdynamic") {
    return renderMatrixDynamic(resposta);
  }

  // Array ou objeto
  if (resposta && typeof resposta === 'object') {
    if (Array.isArray(resposta)) {
      return renderArray(resposta);
    }
    return renderGenericObject(resposta);
  }

  // Texto simples
  return resposta ?? "-";
}